import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * s221 Lane 1d — THE "NOBODY" ANSWER, reported upward (Main.dc.html `none.pick`, the `groups` map).
 *
 * Six of chapter one's ten screens carry a "none" tile, and each DECLARES what picking it does: a
 * `prune` (how far the executor-task counter drops) and, on two screens, a `delta` sentence. The
 * declaration lives in the step model (`shared/interview/chapters.ts`), never as a number in a screen
 * file. The shared TilePicker reports "this screen's none tile is picked" here; the shell reads the
 * sum of the active prunes and the active sentence and draws them — ONE place, so every later screen
 * inherits it the moment it declares a `none`.
 *
 * Outside a provider the report is a no-op, so a tile picker still works on its own.
 */

export type NoneAnswer = { prune: number; delta?: string };

type Ctx = { report: (key: string, answer: NoneAnswer | null) => void; answers: ReadonlyMap<string, NoneAnswer> };
const NoneAnswerContext = createContext<Ctx | null>(null);

export function NoneAnswerProvider({ children }: { children: ReactNode }) {
  const [answers, setAnswers] = useState<ReadonlyMap<string, NoneAnswer>>(() => new Map());
  // STABLE across renders (functional update, no dependency on `answers`), so a reporter's cleanup
  // fires only when it unmounts — never on every change of the map it reports into.
  const report = useCallback((key: string, answer: NoneAnswer | null) =>
    setAnswers((prev) => {
      const had = prev.get(key);
      if (!answer && !had) return prev;
      if (answer && had && had.prune === answer.prune && had.delta === answer.delta) return prev;
      const next = new Map(prev);
      if (answer) next.set(key, answer);
      else next.delete(key);
      return next;
    }), []);
  const value = useMemo<Ctx>(() => ({ answers, report }), [answers, report]);
  return <NoneAnswerContext.Provider value={value}>{children}</NoneAnswerContext.Provider>;
}

/** A tile picker reports whether its none tile is picked, and what that tile declares. */
export function useReportNoneAnswer(key: string, active: boolean, declared: NoneAnswer | undefined) {
  const ctx = useContext(NoneAnswerContext);
  const report = ctx?.report;
  const prune = declared?.prune ?? 0;
  const delta = declared?.delta;
  const on = active && !!declared;
  // Primitive dependencies only: an object here would re-run (clean up, re-report) every render.
  useEffect(() => {
    if (!report) return;
    report(key, on ? { prune, delta } : null);
  }, [report, key, on, prune, delta]);
  useEffect(() => () => report?.(key, null), [report, key]);
}

/** The shell reads what every active none answer declares: the total prune and the sentences to show. */
export function useNoneAnswers(): { prune: number; deltas: string[] } {
  const ctx = useContext(NoneAnswerContext);
  return useMemo(() => {
    const all = Array.from(ctx?.answers.values() ?? []);
    return { prune: all.reduce((n, a) => n + a.prune, 0), deltas: all.map((a) => a.delta).filter((d): d is string => !!d) };
  }, [ctx?.answers]);
}

/** The counter the shell shows: the live total less the declared prunes. Ready never exceeds the total. Pure. */
export function prunedCounts(counts: { total: number; ready: number }, prune: number): { total: number; ready: number } {
  const total = Math.max(0, counts.total - prune);
  return { total, ready: Math.min(counts.ready, total) };
}
